import * as React from "react";
import { Ellipsis } from "lucide-react";
import { Button } from "../../ui/button.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/popover.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CookMenuProps {
  onAddNote: () => void;
  onAddPhoto: () => void;
  onPause: () => void;
  onAbandon: () => void;
}

// ---------------------------------------------------------------------------
// CookMenu
// ---------------------------------------------------------------------------

export function CookMenu({ onAddNote, onAddPhoto, onPause, onAbandon }: CookMenuProps) {
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  function handleAddNote() {
    setPopoverOpen(false);
    onAddNote();
  }

  function handleAddPhoto() {
    setPopoverOpen(false);
    onAddPhoto();
  }

  function handlePause() {
    setPopoverOpen(false);
    onPause();
  }

  function handleAbandonClick() {
    setPopoverOpen(false);
    setConfirmOpen(true);
  }

  function handleConfirmAbandon() {
    setConfirmOpen(false);
    onAbandon();
  }

  function handleCancelAbandon() {
    setConfirmOpen(false);
  }

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="More options"
            className="min-h-[48px] min-w-[48px]"
          >
            <Ellipsis className="h-5 w-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          <div className="flex flex-col gap-0.5">
            <Button
              variant="ghost"
              className="min-h-[48px] w-full justify-start"
              onClick={handleAddNote}
            >
              Add note here
            </Button>
            <Button
              variant="ghost"
              className="min-h-[48px] w-full justify-start"
              onClick={handleAddPhoto}
            >
              Add a step photo
            </Button>
            <Button
              variant="ghost"
              className="min-h-[48px] w-full justify-start"
              onClick={handlePause}
            >
              Pause session
            </Button>
            <Button
              variant="ghost"
              className="min-h-[48px] w-full justify-start text-destructive hover:text-destructive"
              onClick={handleAbandonClick}
            >
              Abandon session
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Abandon this cook session?</DialogTitle>
            <DialogDescription>
              Your progress will be discarded.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelAbandon}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmAbandon}>
              Abandon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
